import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  dashboardPendingFeesData,
  requestPendingFeePayment,
} from "../../../ApiService/ApiService";
import CustomDialog from "../../../Components/CustomDialog";

function PendingFees({ schoolId, sessions, sessionsError, sessionsLoading }) {
  const [sessionId, setSessionId] = useState("");
  const [classData, setClassData] = useState([]);
  const [selectedClass, setSelectedClass] = useState("");
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogMessage, setDialogMessage] = useState("");
  const [dialogButtons, setDialogButtons] = useState([]);

  useEffect(() => {
    if (sessionId) {
      setLoading(true);
      setError(null);
      dashboardPendingFeesData(sessionId, schoolId)
        .then((response) => {
          const data = response.data.data.byClass;
          setClassData(data);
          setLoading(false);
          if (data.length > 0) {
            setSelectedClass(data[0].name);
          }
        })
        .catch((error) => {
          console.error("Error fetching pending fees data:", error);
          setError("Error fetching pending fees data");
          setLoading(false);
        });
    }
  }, [sessionId, schoolId]);

  const handleRequestPayment = (studentId) => {
    requestPendingFeePayment(studentId)
      .then((response) => {
        if (response.status === 200) {
          setDialogTitle("Success");
          setDialogMessage("Request sent successfully");
        } else {
          setDialogTitle("Error");
          setDialogMessage("Failed to send request");
        }
        setDialogButtons([
          {
            name: "Close",
            onPress: () => setDialogVisible(false),
          },
        ]);
        setDialogVisible(true);
      })
      .catch((error) => {
        console.error("Error requesting payment:", error);
        setDialogTitle("Error");
        setDialogMessage("Error requesting payment");
        setDialogButtons([
          {
            name: "Close",
            onPress: () => setDialogVisible(false),
          },
        ]);
        setDialogVisible(true);
      });
  };

  useEffect(() => {
    if (selectedClass) {
      const selectedClassData = classData.find(
        (cls) => cls.name === selectedClass
      );
      if (selectedClassData) {
        setStudents(selectedClassData.list);
      }
    } else {
      setStudents([]);
    }
  }, [selectedClass, classData]);

  useEffect(() => {
    if (sessions?.data?.length > 0) {
      setSessionId(sessions.data[0].id);
    }
  }, [sessions]);

  return (
    <Box
      sx={{
        p: 2,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          display: "flex",
        }}
      >
        <FormControl
          fullWidth
          required
          size="small"
          sx={{
            marginTop: 2,
            width: 200,
          }}
        >
          <InputLabel id="sessions">Sessions</InputLabel>
          <Select
            name="sessionId"
            label="Sessions"
            labelId="sessions"
            value={sessionId}
            onChange={(e) => setSessionId(e.target.value)}
            size="small"
            disabled={sessionsLoading || sessionsError}
          >
            {sessionsLoading ? (
              <MenuItem value="">
                <CircularProgress size={24} />
              </MenuItem>
            ) : sessionsError ? (
              <MenuItem value="">Error fetching sessions</MenuItem>
            ) : (
              sessions?.data?.map((currSession) => (
                <MenuItem key={currSession.id} value={currSession.id}>
                  {currSession.name}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
        <FormControl
          fullWidth
          required
          size="small"
          sx={{ marginTop: 2, marginLeft: 1, width: 200 }}
        >
          <InputLabel id="classes">Classes</InputLabel>
          <Select
            name="classId"
            label="Classes"
            labelId="classes"
            value={selectedClass}
            onChange={(e) => setSelectedClass(e.target.value)}
            size="small"
          >
            {classData.map((cls) => (
              <MenuItem key={cls.name} value={cls.name}>
                {cls.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <CircularProgress size={48} />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <>
          {selectedClass && students.length > 0 ? (
            <TableContainer component={Paper} sx={{ marginTop: 2 }}>
              <Table>
                <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      Registration ID
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      Pending Amount
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Fee Type</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {students.map((student) => (
                    <TableRow key={student.studentId}>
                      <TableCell>{student.registrationId}</TableCell>
                      <TableCell>{student.name}</TableCell>
                      <TableCell sx={{ color: "red" }}>
                        {student.amount}.00
                      </TableCell>
                      <TableCell>{student.feeType}</TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          size="small"
                          sx={{ textTransform: "unset", borderRadius: 2 }}
                          color="primary"
                          onClick={() =>
                            handleRequestPayment(student.studentId)
                          }
                        >
                          Request Payment
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography
              variant="subtitle1"
              align="center"
              sx={{ marginTop: 2 }}
            >
              {selectedClass
                ? "No students found for the selected class"
                : "Please select a class"}
            </Typography>
          )}
        </>
      )}

      <CustomDialog
        visible={dialogVisible}
        title={dialogTitle}
        message={dialogMessage}
        buttons={dialogButtons}
        onClose={(action) => {
          if (action === "Close") {
            setDialogVisible(false);
          }
        }}
      />
    </Box>
  );
}

export default PendingFees;
